import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { generateFacturePDF } from '../lib/generateFacturePDF'

export default function Facturation() {
  const { praticien } = useAuth()
  const [factures, setFactures] = useState([])
  const [patients, setPatients] = useState([])
  const [consultations, setConsultations] = useState([])
  const [loading, setLoading] = useState(false)
  const [panel, setPanel] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [filterAnnee, setFilterAnnee] = useState(new Date().getFullYear())
  const [sortCol, setSortCol] = useState('date_facture')
  const [sortDir, setSortDir] = useState('desc')
  const [filterStatut, setFilterStatut] = useState(null) // null = tout, 'payee', 'en_attente'
  const [sendingEmail, setSendingEmail] = useState(null)
  const [emailSent, setEmailSent] = useState(null)
  const [panelPaiement, setPanelPaiement] = useState(null)
  const [modePaiementRapide, setModePaiementRapide] = useState('cb')
  const [filterMode, setFilterMode] = useState(null) // null = tous, 'especes', 'cheque', 'cb', 'virement'
  const [filterNonDepose, setFilterNonDepose] = useState(false)
  const [panelMail, setPanelMail] = useState(null) // { facture, destinataires, selection }

  function emptyForm() {
    return {
      patient_id: '', consultation_id: '',
      date_facture: new Date().toISOString().slice(0, 10),
      montant: praticien?.tarif_base || 60,
      statut: 'en_attente', mode_paiement: '', date_paiement: '', notes: '',
      gratuite: false
    }
  }

  async function load() {
    if (!praticien?.id) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('factures')
        .select('*')
        .eq('praticien_id', praticien.id)
        .gte('date_facture', `${filterAnnee}-01-01`)
        .lte('date_facture', `${filterAnnee}-12-31`)
        .order('date_facture', { ascending: false })

      if (error) { console.error('Erreur factures:', error); setLoading(false); return }

      // Enrichir avec patients (par lots de 200 pour éviter la limite Supabase)
      const patientIds = [...new Set((data || []).map(f => f.patient_id).filter(Boolean))]
      let patientsMap = {}
      for (let i = 0; i < patientIds.length; i += 200) {
        const batch = patientIds.slice(i, i + 200)
        const { data: pts } = await supabase
          .from('patients').select('id, prenom, nom, adresse, code_postal, ville, email, date_naissance')
          .in('id', batch)
        ;(pts || []).forEach(p => { patientsMap[p.id] = p })
      }

      // Enrichir avec consultations (pour durée dans le PDF)
      const consultIds = [...new Set((data || []).map(f => f.consultation_id).filter(Boolean))]
      let consultsMap = {}
      if (consultIds.length > 0) {
        const { data: consults } = await supabase
          .from('consultations').select('id, duree_minutes, motif')
          .in('id', consultIds)
        ;(consults || []).forEach(c => { consultsMap[c.id] = c })
      }

      setFactures((data || []).map(f => ({
        ...f,
        patients: patientsMap[f.patient_id] || null,
        consultations: f.consultation_id ? consultsMap[f.consultation_id] || null : null
      })))
    } catch (e) {
      console.error('Exception:', e)
    }
    setLoading(false)
  }

  const loadCallback = useCallback(load, [praticien?.id, filterAnnee])

  useEffect(() => { loadCallback() }, [loadCallback])

  useEffect(() => {
    const handler = () => openNew()
    window.addEventListener('cabinet:new', handler)
    return () => window.removeEventListener('cabinet:new', handler)
  }, [])

  useEffect(() => {
    supabase.from('patients').select('id, prenom, nom').order('nom')
      .then(({ data }) => setPatients(data || []))
  }, [])

  async function loadConsultations(patientId) {
    const { data: consults } = await supabase
      .from('consultations')
      .select('id, date_consultation, motif, tarif')
      .eq('patient_id', patientId)
      .eq('praticien_id', praticien.id)
      .order('date_consultation', { ascending: false })
      .limit(20)

    // Récupérer les consultation_id déjà facturés par cette praticienne
    const { data: dejafactures } = await supabase
      .from('factures')
      .select('consultation_id')
      .eq('praticien_id', praticien.id)
      .not('consultation_id', 'is', null)

    const idsFactures = new Set((dejafactures || []).map(f => f.consultation_id).filter(Boolean))
    setConsultations((consults || []).map(c => ({
      ...c,
      dejaFacturee: idsFactures.has(c.id)
    })))
  }

  function openNew() {
    setForm(emptyForm())
    setConsultations([])
    setPanel('new')
  }

  function openEdit(f) {
    setForm({
      patient_id: f.patient_id, consultation_id: f.consultation_id || '',
      date_facture: f.date_facture, montant: f.montant, statut: f.statut,
      mode_paiement: f.mode_paiement || '', date_paiement: f.date_paiement || '', notes: f.notes || '',
      gratuite: f.montant === 0
    })
    if (f.patient_id) loadConsultations(f.patient_id)
    setPanel(f)
  }

  async function encaisserRapide(facture, mode) {
    await supabase.from('factures').update({
      statut: 'payee',
      mode_paiement: mode,
      date_paiement: new Date().toISOString().slice(0, 10)
    }).eq('id', facture.id)
    setPanelPaiement(null)
    load()
  }

  async function deleteFacture(facture) {
    // Appelable depuis le panneau (sans argument) ou depuis une ligne de la liste (avec la facture)
    const cible = (facture && facture.id) ? facture : panel
    if (!cible || cible === 'new') return
    if (!window.confirm(`Supprimer la facture ${cible.numero || ''} ?\nL'écriture comptable (recette) liée sera supprimée en même temps. Action irréversible.`)) return
    const { error } = await supabase.from('factures').delete().eq('id', cible.id)
    if (error) { alert('Erreur suppression : ' + error.message); return }
    // Supprimer l'écriture comptable rattachée (lien facture_id)
    await supabase.from('ecritures_comptables').delete().eq('facture_id', cible.id)
    if (panel && panel !== 'new' && panel.id === cible.id) setPanel(null)
    load()
  }

  async function save() {
    // Vérification serveur : la consultation est-elle déjà facturée ?
    if (form.consultation_id && panel === 'new') {
      const { data: existing } = await supabase
        .from('factures')
        .select('id')
        .eq('consultation_id', form.consultation_id)
        .eq('praticien_id', praticien.id)
        .limit(1)
      if (existing && existing.length > 0) {
        alert('Cette consultation a déjà une facture émise. Impossible de facturer deux fois.')
        return
      }
    }
    setSaving(true)
    const payload = {
      praticien_id: praticien.id,
      patient_id: form.patient_id,
      consultation_id: form.consultation_id || null,
      date_facture: form.date_facture,
      montant: parseFloat(form.montant),
      statut: form.statut,
      mode_paiement: form.mode_paiement || null,
      date_paiement: form.date_paiement || null,
      notes: form.notes
    }
    if (panel === 'new') {
      const annee = new Date().getFullYear()
      const ts = Date.now().toString().slice(-6)
      payload.numero = `FAC-${annee}-${ts}`
      const { data: factureCreee, error: insErr } = await supabase.from('factures').insert(payload).select().single()
      if (insErr) {
        alert('Erreur création facture : ' + insErr.message)
        setSaving(false)
        return
      }
      if (form.statut === 'payee') {
        await supabase.from('ecritures_comptables').insert({
          praticien_id: praticien.id, date_ecriture: form.date_facture,
          libelle: 'Honoraires consultation', type: 'recette',
          categorie: 'Honoraires', montant: parseFloat(form.montant),
          source: 'auto', facture_id: factureCreee.id
        })
      }
    } else {
      await supabase.from('factures').update(payload).eq('id', panel.id)
    }
    setSaving(false)
    setPanel(null)
    loadCallback()
    if (form.patient_id) loadConsultations(form.patient_id)
  }

  // Modifier le mode de paiement d'une facture depuis la liste, sans repasser par la consultation.
  // Ne touche QUE mode_paiement (et le suivi de dépôt) — pas le montant, pas l'écriture comptable.
  async function changeMode(f, mode) {
    if (!mode || mode === f.mode_paiement) return
    const deposable = mode === 'cheque' || mode === 'especes'
    const update = { mode_paiement: mode }
    if (!deposable) { update.depose_banque = false; update.date_depot = null }
    const { error } = await supabase.from('factures').update(update).eq('id', f.id)
    if (error) { alert('Erreur : ' + error.message); return }
    setFactures(prev => prev.map(x => x.id === f.id ? { ...x, ...update } : x))
  }

  // Rassemble les adresses mail : celle du patient + celles des responsables liés (patient_liens).
  async function buildDestinataires(patient) {
    const list = []
    const seen = new Set()
    const add = (email, label, principal) => {
      if (!email) return
      const key = String(email).trim().toLowerCase()
      if (!key || seen.has(key)) return
      seen.add(key)
      list.push({ email: String(email).trim(), label, principal: !!principal })
    }
    if (patient?.email) add(patient.email, `${patient.prenom || ''} ${patient.nom || ''}`.trim() + ' (patient)', false)
    if (patient?.id) {
      const { data: liens } = await supabase.from('patient_liens')
        .select('lie_id, role, facturation_principale').eq('patient_id', patient.id)
      const respIds = (liens || []).map(l => l.lie_id)
      if (respIds.length) {
        const { data: resps } = await supabase.from('patients').select('id, prenom, nom, email').in('id', respIds)
        const map = {}
        ;(resps || []).forEach(r => { map[r.id] = r })
        for (const lien of liens) {
          const r = map[lien.lie_id]
          if (r?.email) add(r.email, `${r.prenom || ''} ${r.nom || ''}`.trim() + (lien.role ? ` (${lien.role})` : ' (responsable)'), lien.facturation_principale)
        }
      }
    }
    return list
  }

  async function onMailClick(f) {
    const destinataires = await buildDestinataires(f.patients)
    if (destinataires.length === 0) {
      alert("Aucune adresse mail enregistrée (ni patient, ni responsable). Envoi impossible.")
      return
    }
    if (destinataires.length === 1) {
      sendFactureByEmail(f, [destinataires[0].email])
      return
    }
    const principaux = destinataires.filter(d => d.principal).map(d => d.email)
    setPanelMail({ facture: f, destinataires, selection: principaux })
  }

  async function sendFactureByEmail(facture, emails) {
    const liste = (emails && emails.length) ? emails : (facture.patients?.email ? [facture.patients.email] : [])
    if (liste.length === 0) {
      alert("Aucune adresse mail. Envoi impossible.")
      return
    }
    setPanelMail(null)
    setSendingEmail(facture.id)
    try {
      const pdfBase64 = await generateFacturePDF(facture, praticien, true)
      let okOne = false
      const echecs = []
      for (const email of liste) {
        try {
          const res = await fetch('https://qzaqbhmtmeokgbdywjgx.supabase.co/functions/v1/send-facture', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              prenomPatient: facture.patients?.prenom,
              numero: facture.numero,
              dateFacture: format(new Date(facture.date_facture), 'dd/MM/yyyy'),
              montant: facture.montant,
              praticienPrenom: praticien.prenom,
              praticienNom: praticien.nom,
              pdfBase64,
              pdfNom: facture.numero + '.pdf',
            })
          })
          if (res.ok) okOne = true
          else { let d = ''; try { d = await res.text() } catch { /* vide */ } echecs.push(email + ' (' + res.status + (d ? ' : ' + d : '') + ')') }
        } catch (e) {
          echecs.push(email + ' : ' + (e?.message || e))
        }
      }
      if (okOne) {
        await supabase.from('factures').update({ email_envoye_at: new Date().toISOString() }).eq('id', facture.id)
        setFactures(prev => prev.map(f => f.id === facture.id ? { ...f, email_envoye_at: new Date().toISOString() } : f))
        setEmailSent(facture.id)
        setTimeout(() => setEmailSent(null), 4000)
      }
      if (echecs.length) alert('Envoi échoué pour :\n- ' + echecs.join('\n- '))
    } catch (e) {
      alert('Erreur lors de l\'envoi : ' + (e?.message || e))
    }
    setSendingEmail(null)
  }

  async function uploadSignature(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      await supabase.from('praticiens')
        .update({ signature_base64: ev.target.result })
        .eq('id', praticien.id)
      alert('Signature enregistree. Elle apparaitra sur vos prochaines factures PDF.')
    }
    reader.readAsDataURL(file)
  }

  function generatePDF(facture, sendAsEmail = false) {
    return new Promise((resolve) => {
    import('jspdf').then(({ jsPDF }) => {
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF({ unit: 'mm', format: 'a4' })
        const pat = facture.patients
        const W = 210
        const margin = 20

        // EN-TETE GAUCHE
        doc.setFontSize(15)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(20, 20, 20)
        doc.text('CABINET OSTEOPATHIE', margin, 28)

        doc.setFontSize(11)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(50, 50, 50)
        doc.text(praticien.prenom + ' ' + praticien.nom, margin, 36)

        let yInfo = 42
        if (praticien.adresse_cabinet) {
          const lignes = doc.splitTextToSize(praticien.adresse_cabinet, 90)
          lignes.forEach(l => { doc.text(l, margin, yInfo); yInfo += 5 })
        }
        if (praticien.telephone) { doc.text('Tel : ' + praticien.telephone, margin, yInfo); yInfo += 5 }
        if (praticien.numero_rpps) { doc.text('N RPPS : ' + praticien.numero_rpps, margin, yInfo); yInfo += 5 }
        if (praticien.siret) { doc.text('SIRET : ' + praticien.siret, margin, yInfo) }

        // EN-TETE DROITE
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(20, 20, 20)
        doc.text('FACTURE N ' + facture.numero, W - margin, 28, { align: 'right' })

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(80, 80, 80)
        doc.text('Date : ' + format(new Date(facture.date_facture), 'dd/MM/yyyy'), W - margin, 36, { align: 'right' })

        // LIGNE SEPARATRICE
        doc.setDrawColor(180, 180, 180)
        doc.setLineWidth(0.3)
        doc.line(margin, 62, W - margin, 62)

        // BLOC PATIENT
        doc.setFontSize(8)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(120, 120, 120)
        doc.text('PATIENT :', margin, 70)

        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(20, 20, 20)
        doc.text((pat?.prenom || '') + ' ' + (pat?.nom || ''), margin, 78)

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(70, 70, 70)
        let yPat = 84
        if (pat?.date_naissance) {
          const age = Math.floor((new Date() - new Date(pat.date_naissance)) / (365.25 * 24 * 3600 * 1000))
          doc.text('Ne(e) le ' + format(new Date(pat.date_naissance), 'dd/MM/yyyy') + ' (' + age + ' ans)', margin, yPat)
          yPat += 5
        }
        if (pat?.adresse) {
          const lignes = doc.splitTextToSize(pat.adresse, 100)
          lignes.forEach(l => { doc.text(l, margin, yPat); yPat += 5 })
        }
        if (pat?.code_postal || pat?.ville) {
          doc.text([pat?.code_postal, pat?.ville].filter(Boolean).join(' '), margin, yPat)
          yPat += 5
        }

        // TAMPON PAYEE supprimé du haut — voir bas de page

        // TABLEAU PRESTATIONS
        const dureeLabel = facture.consultations?.duree_minutes
          ? facture.consultations.duree_minutes + ' min' : '-'
        const startY = Math.max(yPat + 8, 105)

        doc.autoTable({
          startY,
          head: [['Prestation', 'Duree', 'Montant']],
          body: [['Consultation osteopathie', dureeLabel, facture.montant + ' euros']],
          styles: {
            fontSize: 11,
            cellPadding: { top: 6, bottom: 6, left: 5, right: 5 },
            textColor: [20, 20, 20],
            lineColor: [210, 210, 210],
            lineWidth: 0.2,
          },
          headStyles: {
            fillColor: [30, 30, 30],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 10,
          },
          columnStyles: {
            0: { cellWidth: 115 },
            1: { cellWidth: 25, halign: 'center' },
            2: { cellWidth: 30, halign: 'right' },
          },
          margin: { left: margin, right: margin },
          tableWidth: W - margin * 2,
        })

        const yTable = doc.lastAutoTable.finalY

        // TOTAL
        doc.setDrawColor(30, 30, 30)
        doc.setLineWidth(0.5)
        doc.line(margin, yTable + 5, W - margin, yTable + 5)

        doc.setFontSize(13)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(20, 20, 20)
        doc.text('TOTAL :', margin, yTable + 16)
        doc.text(facture.montant + ' euros', W - margin, yTable + 16, { align: 'right' })

        doc.setLineWidth(0.3)
        doc.setDrawColor(180, 180, 180)
        doc.line(margin, yTable + 20, W - margin, yTable + 20)

        // REGLEMENT (sans mode de paiement)
        let yBottom = yTable + 30
        if (facture.statut === 'payee') {
          doc.setFontSize(10)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(100, 100, 100)
          let reglLabel = 'Reglement recu'
          if (facture.date_paiement) {
            reglLabel += ' le ' + format(new Date(facture.date_paiement), 'dd/MM/yyyy')
          }
          doc.text(reglLabel, margin, yBottom)
          yBottom += 8
        }
        if (facture.notes) {
          doc.setFontSize(9)
          doc.setTextColor(120, 120, 120)
          doc.text(facture.notes, margin, yBottom)
          yBottom += 8
        }

        // ZONE SIGNATURE
        const ySig = Math.max(yBottom + 10, yTable + 50)
        if (ySig < 240) {
          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(120, 120, 120)
          doc.text('Signature :', margin, ySig)
          doc.setDrawColor(200, 200, 200)
          doc.setLineWidth(0.3)
          doc.rect(margin, ySig + 3, 60, 28)
          if (praticien.signature_base64) {
            try {
              doc.addImage(praticien.signature_base64, 'PNG', margin + 2, ySig + 5, 56, 24)
            } catch (e) { /* signature invalide */ }
          }
        }

        // TAMPON PAYEE — grand, rouge, en diagonale, en bas de page
        if (facture.statut === 'payee') {
          doc.saveGraphicsState()
          // Centre de rotation : milieu bas de la zone de contenu
          const cx = W / 2
          const cy = 240
          // Rotation -30 degrés
          const angle = -30 * Math.PI / 180
          doc.setGState(new doc.GState({ opacity: 0.18 }))
          // Rectangle rouge en diagonale via transformation
          doc.setFillColor(200, 30, 30)
          doc.setDrawColor(200, 30, 30)
          doc.setLineWidth(2)
          // On dessine le texte en diagonale
          doc.setFontSize(52)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(200, 30, 30)
          doc.text('PAYEE', cx, cy, {
            align: 'center',
            angle: 30,
          })
          doc.restoreGraphicsState()
          // Contour du tampon (rectangle tourné simulé par 4 lignes)
          doc.saveGraphicsState()
          doc.setGState(new doc.GState({ opacity: 0.25 }))
          doc.setDrawColor(200, 30, 30)
          doc.setLineWidth(1.5)
          doc.setFontSize(48)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(200, 30, 30)
          doc.text('PAYEE', cx, cy, {
            align: 'center',
            angle: 30,
            renderingMode: 'stroke',
          })
          doc.restoreGraphicsState()
        }

        // PIED DE PAGE
        doc.setDrawColor(190, 190, 190)
        doc.setLineWidth(0.2)
        doc.line(margin, 272, W - margin, 272)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(140, 140, 140)
        doc.text('TVA non applicable - Article 261 4-1 du CGI', W / 2, 278, { align: 'center' })

        if (sendAsEmail) {
          const pdfBase64 = doc.output('datauristring').split(',')[1]
          resolve(pdfBase64)
        } else {
          doc.save(facture.numero + '.pdf')
          resolve(null)
        }
      })
    })
    })
  }

  const total = factures.reduce((s, f) => s + (f.statut !== 'annulee' ? f.montant : 0), 0)
  const encaisse = factures.filter(f => f.statut === 'payee').reduce((s, f) => s + f.montant, 0)
  const attente = factures.filter(f => f.statut === 'en_attente').reduce((s, f) => s + f.montant, 0)

  // Remise en banque : ce qui est payé en chèque/espèces et pas encore déposé
  const chequesADeposer = factures.filter(f => f.statut === 'payee' && f.mode_paiement === 'cheque' && !f.depose_banque)
  const especesADeposer = factures.filter(f => f.statut === 'payee' && f.mode_paiement === 'especes' && !f.depose_banque)
  const totalChequesADeposer = chequesADeposer.reduce((s, f) => s + f.montant, 0)
  const totalEspecesADeposer = especesADeposer.reduce((s, f) => s + f.montant, 0)

  const MODE_LABELS = { especes: 'Espèces', cheque: 'Chèque', cb: 'CB', virement: 'Virement' }
  const ESPECES_OU_CHEQUE = f => f.mode_paiement === 'cheque' || f.mode_paiement === 'especes'

  async function toggleDepose(f) {
    const newVal = !f.depose_banque
    const dateDepot = newVal ? new Date().toISOString().slice(0, 10) : null
    const { error } = await supabase.from('factures')
      .update({ depose_banque: newVal, date_depot: dateDepot }).eq('id', f.id)
    if (error) { alert('Erreur : ' + error.message); return }
    setFactures(prev => prev.map(x => x.id === f.id ? { ...x, depose_banque: newVal, date_depot: dateDepot } : x))
  }

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir(col === 'date_facture' ? 'desc' : 'asc') }
  }

  const facturesSorted = [...factures]
    .filter(f => filterStatut ? f.statut === filterStatut : true)
    .filter(f => filterMode ? f.mode_paiement === filterMode : true)
    .filter(f => filterNonDepose ? (ESPECES_OU_CHEQUE(f) && f.statut === 'payee' && !f.depose_banque) : true)
    .sort((a, b) => {
    let va = a[sortCol], vb = b[sortCol]
    if (sortCol === 'patient') { va = `${a.patients?.nom} ${a.patients?.prenom}`; vb = `${b.patients?.nom} ${b.patients?.prenom}` }
    if (va < vb) return sortDir === 'asc' ? -1 : 1
    if (va > vb) return sortDir === 'asc' ? 1 : -1
    // Tri secondaire par created_at desc (dernière créée en premier)
    return new Date(b.created_at) - new Date(a.created_at)
  })

  function SortIcon({ col }) {
    if (sortCol !== col) return <span style={{color:'#ccc', marginLeft:4}}>↕</span>
    return <span style={{marginLeft:4}}>{sortDir === 'asc' ? '↑' : '↓'}</span>
  }

  return (
    <>
      <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:12}}>
        <span style={{fontSize:13, color:'var(--text-2)', fontWeight:500}}>Année :</span>
        {[2021,2022,2023,2024,2025,2026].map(y => (
          <button key={y} className="btn btn-sm"
            style={{fontWeight: filterAnnee === y ? 700 : 400, background: filterAnnee === y ? '#1D9E75' : '#f0f0f0', color: filterAnnee === y ? '#fff' : '#333', border: 'none'}}
            onClick={() => setFilterAnnee(y)}>{y}</button>
        ))}
      </div>
      <div className="stats-grid">
        <div className="stat-card" style={{cursor:'pointer', outline: !filterStatut ? '2px solid #1D9E75' : 'none'}} onClick={() => setFilterStatut(null)}>
          <div className="label">Facturé {filterAnnee}</div><div className="value">{total.toFixed(0)} €</div>
        </div>
        <div className="stat-card" style={{cursor:'pointer', outline: filterStatut === 'payee' ? '2px solid #2E7D32' : 'none'}} onClick={() => setFilterStatut(s => s === 'payee' ? null : 'payee')}>
          <div className="label">Encaissé</div><div className="value" style={{color:'#2E7D32'}}>{encaisse.toFixed(0)} €</div>
        </div>
        <div className="stat-card" style={{cursor:'pointer', outline: filterStatut === 'en_attente' ? '2px solid #E65100' : 'none'}} onClick={() => setFilterStatut(s => s === 'en_attente' ? null : 'en_attente')}>
          <div className="label">En attente</div><div className="value" style={{color:'#E65100'}}>{attente.toFixed(0)} €</div>
        </div>
        <div className="stat-card">
          <div className="label">Factures</div><div className="value">{facturesSorted.length}{filterStatut ? ` / ${factures.length}` : ''}</div>
        </div>
      </div>

      <div style={{display:'flex', alignItems:'center', flexWrap:'wrap', gap:10, padding:'10px 14px', background:'#F4F7FB', border:'1px solid #D6E2F0', borderRadius:'var(--radius)', marginBottom:12}}>
        <span style={{fontSize:13, fontWeight:600, color:'#0C447C'}}>À déposer en banque ({filterAnnee}) :</span>
        <span style={{fontSize:13}}>🧾 Chèques <strong>{totalChequesADeposer.toFixed(0)} €</strong> ({chequesADeposer.length})</span>
        <span style={{fontSize:13}}>💶 Espèces <strong>{totalEspecesADeposer.toFixed(0)} €</strong> ({especesADeposer.length})</span>
        <div style={{flex:1}} />
        <span style={{fontSize:12, color:'var(--text-2)'}}>Filtrer :</span>
        {[['', 'Tous'], ['cheque', 'Chèques'], ['especes', 'Espèces'], ['cb', 'CB'], ['virement', 'Virement']].map(([val, lab]) => (
          <button key={val || 'tous'} className="btn btn-sm"
            style={{fontSize:12, fontWeight: (filterMode || '') === val ? 700 : 400, background: (filterMode || '') === val ? '#185FA5' : '#f0f0f0', color: (filterMode || '') === val ? '#fff' : '#333', border:'none'}}
            onClick={() => setFilterMode(val || null)}>{lab}</button>
        ))}
        <label style={{display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer', marginLeft:4}}>
          <input type="checkbox" checked={filterNonDepose} onChange={e => setFilterNonDepose(e.target.checked)} />
          Non déposés seulement
        </label>
      </div>

      <div className="section-header">
        <span className="section-title">Mes factures</span>
        <div style={{display:'flex', gap:8, alignItems:'center'}}>
          <span style={{fontSize:12, color:'var(--text-2)'}}>
            Tarif de base :
            <strong style={{marginLeft:4}}>{praticien?.tarif_base || 60} €</strong>
          </span>
          <button className="btn btn-sm" style={{fontSize:12}} onClick={async () => {
            const nouveau = window.prompt('Nouveau tarif de base (€) :', praticien?.tarif_base || 60)
            if (nouveau && !isNaN(parseFloat(nouveau))) {
              await supabase.from('praticiens').update({ tarif_base: parseFloat(nouveau) }).eq('id', praticien.id)
              window.location.reload()
            }
          }}>
            Modifier
          </button>
          <label style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer'}}>
            <span className="btn btn-sm" style={{fontSize:12}}>
              {praticien?.signature_base64 ? 'Modifier ma signature' : 'Ajouter ma signature'}
            </span>
            <input type="file" accept="image/*" onChange={uploadSignature} style={{display:'none'}} />
          </label>
        </div>
      </div>

      {loading ? <div className="empty">Chargement...</div> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('numero')}>N Facture <SortIcon col="numero" /></th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('patient')}>Patient <SortIcon col="patient" /></th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('date_facture')}>Date <SortIcon col="date_facture" /></th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('montant')}>Montant <SortIcon col="montant" /></th>
                <th>Paiement</th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('statut')}>Statut <SortIcon col="statut" /></th>
                <th>Déposé</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {facturesSorted.length === 0 && <tr><td colSpan={8} className="empty">Aucune facture</td></tr>}
              {facturesSorted.map(f => (
                <tr key={f.id}>
                  <td className="td-muted">{f.numero}</td>
                  <td className="clickable" onClick={() => openEdit(f)}>
                    {f.patients?.prenom} {f.patients?.nom}
                  </td>
                  <td className="td-muted">{format(new Date(f.date_facture), 'dd/MM/yyyy')}</td>
                  <td>{f.montant} euros</td>
                  <td className="td-muted">
                    {f.statut === 'payee' ? (
                      <select value={f.mode_paiement || ''} onChange={e => changeMode(f, e.target.value)}
                        style={{fontSize:12, padding:'2px 4px', border:'1px solid var(--border)', borderRadius:4, background:'var(--surface)'}}
                        title="Modifier le mode de paiement">
                        <option value="" disabled>—</option>
                        <option value="especes">Espèces</option>
                        <option value="cheque">Chèque</option>
                        <option value="cb">CB</option>
                        <option value="virement">Virement</option>
                      </select>
                    ) : (MODE_LABELS[f.mode_paiement] || (f.mode_paiement || '—'))}
                  </td>
                  <td>
                    {f.statut === 'en_attente' ? (
                      <span
                        onClick={e => { e.stopPropagation(); setPanelPaiement(f); setModePaiementRapide('cb') }}
                        style={{cursor:'pointer', display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:12, fontSize:11, fontWeight:700, background:'#FFF3E0', color:'#BF360C', border:'1px solid #FFB74D', userSelect:'none', whiteSpace:'nowrap'}}>
                        ⚡ En attente — cliquer pour encaisser
                      </span>
                    ) : (
                      <span style={{
                        display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:12,
                        fontSize:11, fontWeight:600, whiteSpace:'nowrap',
                        background: f.statut === 'payee' ? '#E8F5E9' : '#FFEBEE',
                        color: f.statut === 'payee' ? '#2E7D32' : '#C62828',
                        border: `1px solid ${f.statut === 'payee' ? '#A5D6A7' : '#FFCDD2'}`
                      }}>
                        {f.statut === 'payee' ? '✓ Payée' : 'Annulée'}
                      </span>
                    )}
                  </td>
                  <td>
                    {ESPECES_OU_CHEQUE(f) && f.statut === 'payee' ? (
                      <label style={{display:'inline-flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:12}}
                        title={f.date_depot ? `Déposé le ${format(new Date(f.date_depot), 'dd/MM/yyyy')}` : 'Pas encore déposé'}>
                        <input type="checkbox" checked={!!f.depose_banque} onChange={() => toggleDepose(f)} />
                        {f.depose_banque ? <span style={{color:'#2E7D32'}}>déposé</span> : <span style={{color:'#C62828', fontWeight:600}}>à déposer</span>}
                      </label>
                    ) : (
                      <span className="td-muted">—</span>
                    )}
                  </td>
                  <td>
                    <div style={{display:'flex', gap:6}}>
                      <button className="btn btn-sm" onClick={() => generateFacturePDF(f, praticien)}>PDF</button>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: f.email_envoye_at ? '#E8F5E9' : emailSent === f.id ? '#EAF3DE' : 'transparent',
                          color: f.email_envoye_at ? '#2E7D32' : emailSent === f.id ? '#27500A' : 'var(--text-2)',
                          border: f.email_envoye_at ? '1px solid #A5D6A7' : 'none',
                        }}
                        onClick={() => onMailClick(f)}
                        disabled={sendingEmail === f.id}
                        title={f.email_envoye_at ? `Envoyé le ${format(new Date(f.email_envoye_at), 'dd/MM/yyyy')}` : 'Envoyer la facture par mail'}
                      >
                        {sendingEmail === f.id ? '...' : f.email_envoye_at ? '✓ Mail' : emailSent === f.id ? '✓' : 'Mail'}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        style={{color:'#C62828', border:'1px solid #FFCDD2', background:'#FFEBEE'}}
                        onClick={() => deleteFacture(f)}
                        title="Supprimer la facture et sa recette comptable"
                      >🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {panel && (
        <>
          <div className="panel-overlay" onClick={() => setPanel(null)} />
          <div className="panel">
            <div className="panel-header">
              <h3>{panel === 'new' ? 'Nouvelle facture' : 'Facture ' + panel.numero}</h3>
              <button className="close-btn" onClick={() => setPanel(null)}>X</button>
            </div>
            <div className="panel-body">
              <div className="form-group" style={{marginBottom:14}}>
                <label>Patient *</label>
                <select value={form.patient_id} onChange={e => {
                  setForm({...form, patient_id: e.target.value, consultation_id: ''})
                  if (e.target.value) loadConsultations(e.target.value)
                }}>
                  <option value="">Selectionner un patient...</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.prenom} {p.nom}</option>)}
                </select>
              </div>

              {consultations.length > 0 && (
                <div className="form-group" style={{marginBottom:14}}>
                  <label>Consultation associee</label>
                  <select value={form.consultation_id} onChange={e => {
                    const c = consultations.find(c => c.id === e.target.value)
                    if (c?.dejaFacturee) return
                    setForm({...form, consultation_id: e.target.value, montant: c?.tarif || form.montant})
                  }}>
                    <option value="">Non liee a une consultation</option>
                    {consultations.map(c => (
                      <option key={c.id} value={c.id} disabled={c.dejaFacturee}>
                        {format(new Date(c.date_consultation), 'dd/MM/yyyy')} - {c.motif || 'Consultation'} - {c.tarif} euros
                        {c.dejaFacturee ? ' (deja facturee)' : ''}
                      </option>
                    ))}
                  </select>
                  {form.consultation_id && consultations.find(c => c.id === form.consultation_id)?.dejaFacturee && (
                    <div style={{fontSize:12, color:'var(--red-text)', marginTop:4}}>
                      Cette consultation est deja facturee.
                    </div>
                  )}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Date facture</label>
                  <input type="date" value={form.date_facture} onChange={e => setForm({...form, date_facture: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Montant (euros)</label>
                  <input
                    type="number"
                    value={form.montant}
                    onChange={e => setForm({...form, montant: e.target.value})}
                    disabled={form.gratuite}
                    style={form.gratuite ? {opacity:.5} : {}}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Statut</label>
                  <select value={form.statut} onChange={e => setForm({...form, statut: e.target.value})}>
                    <option value="en_attente">En attente</option>
                    <option value="payee">Payee</option>
                    <option value="annulee">Annulee</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Mode de paiement</label>
                  <select value={form.mode_paiement} onChange={e => setForm({...form, mode_paiement: e.target.value})}>
                    <option value="">-</option>
                    <option value="especes">Especes</option>
                    <option value="cheque">Cheque</option>
                    <option value="virement">Virement</option>
                    <option value="cb">Carte bancaire</option>
                  </select>
                </div>
              </div>
              {form.statut === 'payee' && (
                <div className="form-group" style={{marginBottom:14}}>
                  <label>Date de paiement</label>
                  <input type="date" value={form.date_paiement} onChange={e => setForm({...form, date_paiement: e.target.value})} />
                </div>
              )}

              {/* Option consultation gratuite */}
              <div style={{display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'var(--bg)', borderRadius:'var(--radius)', marginBottom:14}}>
                <input
                  type="checkbox"
                  id="gratuite"
                  checked={form.gratuite || false}
                  onChange={e => setForm({...form, gratuite: e.target.checked, montant: e.target.checked ? 0 : 60, statut: e.target.checked ? 'payee' : form.statut})}
                  style={{width:15, height:15, cursor:'pointer'}}
                />
                <label htmlFor="gratuite" style={{fontSize:13, cursor:'pointer', margin:0, fontWeight:500}}>
                  Consultation gratuite (montant 0 euro)
                </label>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </div>
            <div className="panel-footer">
              <button className="btn btn-sm" onClick={() => setPanel(null)}>Annuler</button>
              {panel !== 'new' && (
                <button className="btn btn-sm btn-danger" onClick={deleteFacture}
                  style={{color:'#C62828', border:'1px solid #FFCDD2', background:'#FFEBEE'}}>
                  🗑 Supprimer
                </button>
              )}
              <div style={{flex:1}} />
              {panel !== 'new' && (
                <button
                  className="btn btn-sm"
                  style={{background: emailSent === panel?.id ? '#EAF3DE' : 'transparent', color: emailSent === panel?.id ? '#27500A' : 'var(--text-2)'}}
                  onClick={() => onMailClick(panel)}
                  disabled={sendingEmail === panel?.id}
                >
                  {sendingEmail === panel?.id ? '...' : emailSent === panel?.id ? '✓ Envoye' : '✉ Envoyer par mail'}
                </button>
              )}
              <button className="btn btn-primary btn-sm" onClick={save}
                disabled={saving || !form.patient_id}>
                {saving ? 'Enregistrement...' : panel === 'new' ? 'Creer la facture' : 'Enregistrer'}
              </button>
              {panel === 'new' && (
                <button
                  className="btn btn-sm"
                  style={{background:'#185FA5', color:'#fff', border:'none', fontWeight:500}}
                  onClick={async () => {
                    await save()
                    // Après save, la facture est créée — on cherche la dernière pour l'envoyer
                    const { data } = await supabase
                      .from('factures')
                      .select('*, patients(prenom, nom, email, date_naissance)')
                      .eq('praticien_id', praticien.id)
                      .order('created_at', { ascending: false })
                      .limit(1)
                      .single()
                    if (data) onMailClick(data)
                  }}
                  disabled={saving || !form.patient_id}
                >
                  ✉ Creer + Envoyer
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Panel encaissement rapide */}
      {panelPaiement && (
        <>
          <div className="panel-overlay" onClick={() => setPanelPaiement(null)} />
          <div className="panel" style={{width:360}}>
            <div className="panel-header">
              <h3>Encaisser la facture</h3>
              <button className="close-btn" onClick={() => setPanelPaiement(null)}>✕</button>
            </div>
            <div className="panel-body">
              <div style={{fontSize:14, marginBottom:16}}>
                <strong>{panelPaiement.patients?.prenom} {panelPaiement.patients?.nom}</strong>
                <span style={{color:'var(--text-3)', marginLeft:8}}>{panelPaiement.montant} € · {panelPaiement.numero}</span>
              </div>
              <div className="form-group">
                <label>Mode de paiement</label>
                <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                  {[['cb','CB'],['cheque','Chèque'],['especes','Espèces'],['virement','Virement']].map(([val, label]) => (
                    <button key={val}
                      onClick={() => setModePaiementRapide(val)}
                      className="btn btn-sm"
                      style={{fontWeight: modePaiementRapide === val ? 700 : 400, background: modePaiementRapide === val ? 'var(--primary)' : '', color: modePaiementRapide === val ? '#fff' : '', border: modePaiementRapide === val ? 'none' : ''}}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="panel-footer">
              <button className="btn btn-sm" onClick={() => setPanelPaiement(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm"
                style={{background:'#1D9E75', border:'none'}}
                onClick={() => encaisserRapide(panelPaiement, modePaiementRapide)}>
                ✓ Marquer comme payée
              </button>
            </div>
          </div>
        </>
      )}

      {/* Panel sélection des destinataires mail */}
      {panelMail && (
        <>
          <div className="panel-overlay" onClick={() => setPanelMail(null)} />
          <div className="panel" style={{width:420}}>
            <div className="panel-header">
              <h3>Envoyer la facture par mail</h3>
              <button className="close-btn" onClick={() => setPanelMail(null)}>✕</button>
            </div>
            <div className="panel-body">
              <div style={{fontSize:14, marginBottom:12}}>
                <strong>{panelMail.facture.patients?.prenom} {panelMail.facture.patients?.nom}</strong>
                <span style={{color:'var(--text-3)', marginLeft:8}}>{panelMail.facture.montant} € · {panelMail.facture.numero}</span>
              </div>
              <label style={{fontSize:12, color:'var(--text-2)', fontWeight:500}}>Destinataire(s) :</label>
              <div style={{display:'flex', flexDirection:'column', gap:6, marginTop:6}}>
                {panelMail.destinataires.map(d => (
                  <label key={d.email} style={{display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer'}}>
                    <input type="checkbox"
                      checked={panelMail.selection.includes(d.email)}
                      onChange={e => setPanelMail(pm => ({
                        ...pm,
                        selection: e.target.checked
                          ? [...pm.selection, d.email]
                          : pm.selection.filter(x => x !== d.email)
                      }))} />
                    <span>{d.label}{d.principal ? ' ★' : ''} — <span style={{color:'var(--text-2)'}}>{d.email}</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div className="panel-footer">
              <button className="btn btn-sm" onClick={() => setPanelMail(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm" style={{background:'#185FA5', border:'none'}}
                disabled={panelMail.selection.length === 0}
                onClick={() => sendFactureByEmail(panelMail.facture, panelMail.selection)}>
                Envoyer ({panelMail.selection.length})
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
